import { eventBus } from '@freelancelabs/shared';

import { Checkbox, CheckboxSize } from '../input';
import { CheckboxProps } from '../input';

/**
 * **Careful: the option type says `CheckboxProps`, and only a handful of them land.**
 * Extending the props of the control is what lets a caller *type* `title`, `children`
 * or `id` on an option; the loop below then forwards a chosen few, so anything else
 * is accepted by the compiler and silently dropped at runtime. `children` and `title`
 * are forwarded since PCG-135, which needed a qualifier rendered beside the label of
 * a locked option. Widen this list rather than working around it in a caller: the
 * next prop to go missing will look exactly like a bug in `Checkbox`.
 */
export interface CheckboxGroupPropsOptions<T> extends CheckboxProps {
    value: T;
}

export interface CheckboxGroupProps<T> {
    options: CheckboxGroupPropsOptions<T>[];
    values: T[];
    onChange: (arg: T[]) => void;
    size?: CheckboxSize;
    onChangeEventName?: string;
}

const CheckboxGroup = <T,>({ options, onChange, values, size, onChangeEventName }: CheckboxGroupProps<T>) => {
    const handleOnChange = (id: T) => {
        if (values.includes(id)) {
            const data = [...values];
            data.splice(data.indexOf(id), 1);
            onChange(data);
            if (onChangeEventName) {
                eventBus.emit(onChangeEventName, { id, isAdded: false });
            }
            return;
        }
        onChange([...values, id]);
        if (onChangeEventName) {
            eventBus.emit(onChangeEventName, { id, isAdded: true });
        }
        return;
    };
    return (
        <div className="checkbox-group">
            {options.map((option) => {
                const key = option.value?.toString();
                return (
                    <Checkbox
                        key={key}
                        label={option.label}
                        title={option.title}
                        size={size}
                        checked={values.includes(option.value as T)}
                        disabled={option.disabled}
                        onChange={() => handleOnChange(option.value as T)}
                    >
                        {option.children}
                    </Checkbox>
                );
            })}
        </div>
    );
};

export default CheckboxGroup;
